// The one-page application in English, Simplified Chinese and Spanish.
//
// Only the page itself is translated: the emails, and the Consumer Connect
// site the borrower goes on to, stay in English. Each entry is
// [English, 简体中文, Español]; {name} placeholders are filled by translate().

import React, { useContext } from "react";

export type Lang = "en" | "zh" | "es";

export const LANGS: { value: Lang; label: string }[] = [
  { value: "en", label: "English" },
  { value: "zh", label: "中文" },
  { value: "es", label: "Español" },
];

const S = {
  // Page
  tabTitle: ["Mortgage Application", "房贷申请", "Solicitud de hipoteca"],
  language: ["Language", "语言", "Idioma"],
  secure: ["Secure, encrypted application", "安全加密的申请", "Solicitud segura y cifrada"],
  questions: ["Questions?", "有疑问？", "¿Preguntas?"],
  callLO: ["Call your loan officer", "致电您的贷款专员", "Llame a su oficial de préstamos"],
  pageTitle: ["Start your mortgage application", "开始您的房贷申请", "Comience su solicitud de hipoteca"],
  yourLender: ["your lender", "您的贷款机构", "su prestamista"],

  // Loan officer card
  yourLO: ["Your loan officer", "您的贷款专员", "Su oficial de préstamos"],
  call: ["Call", "致电", "Llamar"],
  email: ["Email", "邮件", "Correo"],
  emailCopied: ["Email address copied: {email}", "邮箱地址已复制：{email}", "Correo electrónico copiado: {email}"],
  emailShown: ["Email your loan officer at {email}", "请发邮件给您的贷款专员：{email}", "Escriba a su oficial de préstamos a {email}"],

  // Sections and progress
  secAbout: ["About you", "您的信息", "Sus datos"],
  secLoan: ["Your loan", "您的贷款", "Su préstamo"],
  secProperty: ["Subject property", "贷款房产", "Propiedad del préstamo"],
  secDeclarations: ["Declarations", "声明事项", "Declaraciones"],
  secDocuments: ["Documents", "文件", "Documentos"],
  optional: ["Optional", "选填", "Opcional"],
  optionalParen: ["(optional)", "（选填）", "(opcional)"],
  yourApplication: ["Your application", "您的申请", "Su solicitud"],
  progress: [
    "{done} of {total} sections complete · about 2 min",
    "已完成 {done}/{total} 部分 · 约 2 分钟",
    "{done} de {total} secciones completas · unos 2 min",
  ],
  sectionsNav: ["Application sections", "申请各部分", "Secciones de la solicitud"],
  step: ["Step {n} of {total}", "第 {n} 步，共 {total} 步", "Paso {n} de {total}"],
  about2min: ["About 2 min", "约 2 分钟", "Unos 2 min"],
  answeredCount: ["{answered} of {total} answered", "已回答 {answered}/{total}", "{answered} de {total} respondidas"],

  // About you
  aboutSub: ["How your loan officer will reach you", "方便贷款专员与您联系", "Cómo le contactará su oficial de préstamos"],
  firstName: ["First name", "名", "Nombre"],
  lastName: ["Last name", "姓", "Apellido"],
  emailLabel: ["Email", "电子邮箱", "Correo electrónico"],
  mobilePhone: ["Mobile phone", "手机号码", "Teléfono móvil"],
  ssn: ["Social Security number", "社会安全号码 (SSN)", "Número de Seguro Social"],
  ssnExtra: [
    "Encrypted. Only used for a credit check once you approve it.",
    "已加密。仅在您同意后用于查询信用。",
    "Cifrado. Solo se usa para una consulta de crédito cuando usted la autorice.",
  ],
  ssnShow: ["Show Social Security number", "显示社会安全号码", "Mostrar el Número de Seguro Social"],
  ssnHide: ["Hide Social Security number", "隐藏社会安全号码", "Ocultar el Número de Seguro Social"],
  creditScore: ["Estimated credit score", "预估信用分", "Puntaje de crédito estimado"],
  creditScoreExtra: ["A best guess is fine.", "大概估计即可。", "Un cálculo aproximado es suficiente."],
  selectRange: ["Select a range", "选择区间", "Seleccione un rango"],
  below640: ["Below 640", "640 以下", "Menos de 640"],
  notSure: ["Not sure", "不确定", "No estoy seguro"],
  coToggleTitle: ["Applying with someone else?", "与他人共同申请？", "¿Solicita con otra persona?"],
  coToggleSub: [
    "Add a co-borrower, like a spouse or partner",
    "添加共同借款人，例如配偶或伴侣",
    "Agregue un coprestatario, como su cónyuge o pareja",
  ],
  addCoBorrower: ["Add a co-borrower", "添加共同借款人", "Agregar un coprestatario"],
  coBorrower: ["Co-borrower", "共同借款人", "Coprestatario"],
  encryptedNote: [
    "Your information is encrypted. We won't run your credit without your permission.",
    "您的信息已加密。未经您的许可，我们不会查询您的信用。",
    "Su información está cifrada. No consultaremos su crédito sin su permiso.",
  ],
  reqFirstName: ["Please enter first name", "请输入名", "Ingrese el nombre"],
  reqLastName: ["Please enter last name", "请输入姓", "Ingrese el apellido"],
  reqEmail: ["Please enter email", "请输入电子邮箱", "Ingrese el correo electrónico"],
  badEmail: ["Please enter a valid email", "请输入有效的电子邮箱", "Ingrese un correo electrónico válido"],
  reqPhone: ["Please enter phone number", "请输入电话号码", "Ingrese el número de teléfono"],
  badPhone: ["Please enter a valid phone number", "请输入有效的电话号码", "Ingrese un número de teléfono válido"],
  badSsn: [
    "Please enter a 9-digit Social security number",
    "请输入 9 位社会安全号码",
    "Ingrese un Número de Seguro Social de 9 dígitos",
  ],
  reqCoFirstName: ["Please enter co-borrower first name", "请输入共同借款人的名", "Ingrese el nombre del coprestatario"],
  reqCoLastName: ["Please enter co-borrower last name", "请输入共同借款人的姓", "Ingrese el apellido del coprestatario"],
  reqCoEmail: ["Please enter co-borrower email", "请输入共同借款人的电子邮箱", "Ingrese el correo electrónico del coprestatario"],
  reqCoPhone: [
    "Please enter co-borrower phone number",
    "请输入共同借款人的电话号码",
    "Ingrese el número de teléfono del coprestatario",
  ],

  // Your loan
  loanSub: ["What you're looking to do", "您想办理的业务", "Lo que desea hacer"],
  iWouldLike: ["I'd like to", "我想要", "Quiero"],
  buyHome: ["Buy a home", "买房", "Comprar una casa"],
  buyHomeSub: ["Purchase a new property", "购买新房产", "Comprar una nueva propiedad"],
  refinance: ["Refinance", "再融资", "Refinanciar"],
  refinanceSub: ["Lower my rate or take cash out", "降低利率或套现", "Bajar mi tasa o sacar efectivo"],
  purchasePrice: ["Purchase price", "购房价格", "Precio de compra"],
  downPayment: ["Down payment", "首付款", "Pago inicial"],
  downPaymentTooBig: [
    "The down payment can't be more than the purchase price",
    "首付款不能高于购房价格",
    "El pago inicial no puede ser mayor que el precio de compra",
  ],
  firstTimeBuyer: ["Are you a first-time home buyer?", "您是首次购房者吗？", "¿Es usted comprador de vivienda por primera vez?"],
  yes: ["Yes", "是", "Sí"],
  no: ["No", "否", "No"],
  remainingBalance: ["Remaining balance on the mortgage", "房贷剩余本金", "Saldo pendiente de la hipoteca"],
  currentRate: ["Current interest rate", "当前利率", "Tasa de interés actual"],
  cashOut: ["Borrow additional cash", "额外套现金额", "Efectivo adicional a pedir prestado"],
  secondMortgage: ["Do you have a second mortgage?", "您有第二顺位房贷吗？", "¿Tiene una segunda hipoteca?"],

  // Subject property
  stillShopping: [
    "I'm still shopping and don't have a property address yet",
    "我还在看房，暂时没有房产地址",
    "Todavía estoy buscando y aún no tengo la dirección de la propiedad",
  ],
  streetAddress: ["Street address", "街道地址", "Dirección"],
  streetExtra: [
    "Pick a suggestion and we'll fill in city, state, county and ZIP.",
    "从建议中选择，我们会自动填写城市、州、县和邮编。",
    "Elija una sugerencia y completaremos la ciudad, el estado, el condado y el código postal.",
  ],
  addressPlaceholder: ["Start typing an address", "输入地址", "Empiece a escribir una dirección"],
  addressGood: ["Looks good!", "地址有效！", "¡Se ve bien!"],
  addressBad: ["Please provide your street address.", "请填写街道地址。", "Indique la dirección de la calle."],
  unit: ["Unit", "单元号", "Unidad"],
  unitPlaceholder: ["Apt, suite", "公寓号、套房号", "Apto., suite"],
  city: ["City", "城市", "Ciudad"],
  state: ["State", "州", "Estado"],
  county: ["County", "县", "Condado"],
  zip: ["ZIP", "邮编", "Código postal"],
  select: ["Select", "请选择", "Seleccione"],
  propertyValue: ["Estimated property value", "预估房产价值", "Valor estimado de la propiedad"],
  propertyUse: ["How will you use it?", "房产用途", "¿Cómo la usará?"],
  usePrimary: ["Primary residence", "自住（主要居所）", "Residencia principal"],
  useSecond: ["Second home", "第二住所", "Segunda vivienda"],
  useInvestment: ["Investment property", "投资房", "Propiedad de inversión"],
  useFha: ["FHA secondary residence", "FHA 第二居所", "Residencia secundaria FHA"],

  // Declarations
  declSub: [
    "Standard questions every lender is required to ask",
    "每家贷款机构都必须询问的标准问题",
    "Preguntas estándar que todo prestamista debe hacer",
  ],
  borrower: ["Borrower", "借款人", "Prestatario"],
  answeredInProperty: [
    "(answered in Subject property)",
    "（已在“贷款房产”中回答）",
    "(respondida en Propiedad del préstamo)",
  ],
  selectAnswer: ["Please select an answer", "请选择一个答案", "Seleccione una respuesta"],
  ownedAnother: [
    "Have you had an ownership interest in another property in the last three years?",
    "过去三年内，您是否拥有过其他房产的产权？",
    "¿Ha tenido participación en la propiedad de otro inmueble en los últimos tres años?",
  ],
  ownedType: ["What type of property did you own?", "您拥有的是哪种类型的房产？", "¿Qué tipo de propiedad tenía?"],
  reqOwnedType: ["Please select property type", "请选择房产类型", "Seleccione el tipo de propiedad"],
  typeSecondary: ["Secondary home", "第二住所", "Segunda vivienda"],
  typeInvestment: ["Investment", "投资房", "Inversión"],
  titleHeld: ["How did you hold title to the property?", "您以何种方式持有该房产的产权？", "¿Cómo tenía el título de la propiedad?"],
  reqTitleHeld: ["Please select how you held title", "请选择产权持有方式", "Seleccione cómo tenía el título"],
  titleSole: ["By yourself", "单独持有", "Solo(a)"],
  titleSpouse: ["Jointly with your spouse", "与配偶共同持有", "Conjuntamente con su cónyuge"],
  titleOther: ["Jointly with another person", "与他人共同持有", "Conjuntamente con otra persona"],
  borrowedAmount: ["What is the amount of this money?", "这笔款项的金额是多少？", "¿Cuál es el monto de este dinero?"],
  reqAmount: ["Please enter the amount", "请输入金额", "Ingrese el monto"],
  amountPlaceholder: ["Enter amount", "输入金额", "Ingrese el monto"],
  declFootnote: [
    'These questions come from the Uniform Residential Loan Application (Form 1003). Answering "Yes" doesn\'t disqualify you — your loan officer will follow up.',
    "这些问题来自统一住宅贷款申请表（Form 1003）。回答“是”并不会让您失去资格，您的贷款专员会跟进。",
    "Estas preguntas provienen de la Solicitud Uniforme de Préstamo Residencial (Formulario 1003). Responder «Sí» no lo descalifica: su oficial de préstamos le dará seguimiento.",
  ],

  // Documents
  docsSub: [
    "Upload your documents now to speed up your approval.",
    "现在上传文件，加快审批进度。",
    "Suba sus documentos ahora para agilizar su aprobación.",
  ],
  chooseFiles: ["Choose files", "选择文件", "Elija archivos"],
  orDragHere: [" or drag them here", "，或拖到这里", " o arrástrelos aquí"],
  dropSub: ["PDF, JPG or PNG · phone photos work too", "支持 PDF、JPG、PNG · 手机拍照也可以", "PDF, JPG o PNG · también sirven fotos del teléfono"],
  ready: ["Ready", "已就绪", "Listo"],
  removeFile: ["Remove {name}", "移除 {name}", "Quitar {name}"],
  forExample: ["For example", "例如", "Por ejemplo"],
  exPaystubs: ["Pay stubs", "工资单", "Talones de pago"],
  exW2: ["W-2s / 1099s", "W-2 / 1099", "W-2 / 1099"],
  exBank: ["Bank statements", "银行对账单", "Estados de cuenta bancarios"],
  exLicense: ["Driver’s license", "驾照", "Licencia de conducir"],
  exTaxes: ["Tax returns", "报税表", "Declaraciones de impuestos"],
  exInsurance: ["Homeowners insurance / HOA", "房屋保险 / HOA 物业费", "Seguro de vivienda / HOA"],
  exPurchase: ["Purchase agreement", "购房合同", "Contrato de compraventa"],
  yourDocuments: ["Your documents", "您的文件", "Sus documentos"],
  coDocuments: ["Co-borrower's documents", "共同借款人的文件", "Documentos del coprestatario"],
  onlyImagesPdf: ["You can only upload image and PDF files.", "只能上传图片和 PDF 文件。", "Solo puede subir imágenes y archivos PDF."],

  // Submitting
  consent: [
    "By submitting, you agree that {lender} may contact you about your application. Submitting does not authorize a credit check or lock a rate.",
    "提交即表示您同意 {lender} 就您的申请与您联系。提交并不代表授权查询信用，也不会锁定利率。",
    "Al enviar, acepta que {lender} se comunique con usted sobre su solicitud. Enviar no autoriza una consulta de crédito ni fija una tasa.",
  ],
  completeHighlighted: [
    "Please complete the highlighted fields before continuing.",
    "请先填写标出的字段再继续。",
    "Complete los campos resaltados antes de continuar.",
  ],
  submitting: ["Submitting…", "正在提交…", "Enviando…"],
  submitApplication: ["Submit application", "提交申请", "Enviar solicitud"],
  back: ["Back", "上一步", "Atrás"],
  continue: ["Continue", "继续", "Continuar"],
  saveFailed: [
    "Failed to save application data, please try again",
    "申请保存失败，请重试",
    "No se pudo guardar la solicitud; inténtelo de nuevo",
  ],
  submitFailed: ["Failed to submit application, please try again", "申请提交失败，请重试", "No se pudo enviar la solicitud; inténtelo de nuevo"],
  uploadFailed: [
    "Failed to upload documents, please try again",
    "文件上传失败，请重试",
    "No se pudieron subir los documentos; inténtelo de nuevo",
  ],
  submitted: ["Application submitted successfully!", "申请已成功提交！", "¡Solicitud enviada con éxito!"],
  submissionFailed: ["Submission failed, please try again later", "提交失败，请稍后再试", "El envío falló; inténtelo más tarde"],

  // Thank-you screen
  doneTitle: ["Thank you for submitting your application!", "感谢您提交申请！", "¡Gracias por enviar su solicitud!"],
  doneLede: [
    "Your loan officer has been notified and will contact you shortly.",
    "我们已通知您的贷款专员，他们会尽快与您联系。",
    "Se ha notificado a su oficial de préstamos, quien se comunicará con usted pronto.",
  ],
  yourProgress: ["Your progress", "您的进度", "Su progreso"],
  appSubmitted: ["Application submitted", "申请已提交", "Solicitud enviada"],
  done: ["Done", "已完成", "Listo"],
  nextStepEmail: ["Next step: check your email", "下一步：查看您的邮箱", "Siguiente paso: revise su correo"],
  actionNeeded: ["Action needed", "需要您操作", "Acción necesaria"],
  nextStepText: [
    "Check your email for an activation link to review your application and access your loan documents securely.",
    "请在邮箱中查找激活链接，以便查看您的申请并安全访问您的贷款文件。",
    "Busque en su correo un enlace de activación para revisar su solicitud y acceder de forma segura a los documentos de su préstamo.",
  ],
  sentTo: ["Sent to", "已发送至", "Enviado a"],
  checkSpam: [
    "Don't see it? Check your spam or promotions folder.",
    "没收到？请查看垃圾邮件或推广邮件文件夹。",
    "¿No lo ve? Revise su carpeta de spam o de promociones.",
  ],
  sending: ["Sending…", "正在发送…", "Enviando…"],
  resendEmail: ["Resend email", "重新发送邮件", "Reenviar correo"],
  // The borrower portal hand-off (customers without Consumer Connect)
  portalTitle: ["Track your application", "跟踪申请进度", "Siga su solicitud"],
  portalText: [
    "Sign in to your borrower portal to follow your application, upload documents, and reach your loan officer. Use the email you applied with; if you have no password yet, we email you a one-time code.",
    "登录借款人门户即可查看申请进度、上传文件并联系您的贷款专员。请使用申请时填写的邮箱登录；如果您尚未设置密码，我们会把一次性验证码发到您的邮箱。",
    "Inicie sesión en su portal de prestatario para seguir su solicitud, subir documentos y comunicarse con su oficial de préstamos. Use el correo con el que solicitó; si aún no tiene contraseña, le enviamos un código de un solo uso por correo.",
  ],
  portalSignInAs: ["Sign in with", "登录邮箱", "Inicie sesión con"],
  disclosures: ["Disclosures", "披露信息", "Divulgaciones"],
  privacyPolicy: ["Privacy Policy", "隐私政策", "Política de privacidad"],
  termsAndConditions: ["Terms and Conditions", "条款与条件", "Términos y condiciones"],
  nmlsConsumerAccess: ["NMLS Consumer Access", "NMLS 消费者查询", "Acceso del consumidor NMLS"],
  complaintsLine: [
    "If you have any complaints, please let us know:",
    "如有任何投诉，请联系我们：",
    "Si tiene alguna queja, háganoslo saber:",
  ],
  complaintsShort: ["Complaints:", "投诉：", "Quejas:"],
  legalLinks: ["Legal", "法律信息", "Legal"],
  footerMore: ["More", "更多", "Más"],
  footerLess: ["Less", "收起", "Menos"],
  openPortal: ["Open my application", "查看我的申请", "Abrir mi solicitud"],
  resendSent: [
    "Sent again. It can take a minute or two to arrive.",
    "已重新发送，可能需要一两分钟才能收到。",
    "Enviado de nuevo. Puede tardar uno o dos minutos en llegar.",
  ],
  resendPending: [
    "Your email is still on its way. It can take a few minutes after you submit.",
    "邮件还在路上，提交后可能需要几分钟才能收到。",
    "Su correo todavía está en camino. Puede tardar unos minutos después de enviar la solicitud.",
  ],
  resendRecent: ["We just sent it. Give it a minute to arrive.", "刚刚已发送，请稍等一分钟。", "Acabamos de enviarlo. Espere un minuto a que llegue."],
  resendSubmitted: [
    "You've already finished your application, so there's nothing left to activate.",
    "您已完成申请，无需再激活。",
    "Ya completó su solicitud, así que no hay nada más que activar.",
  ],
  resendError: [
    "We couldn't resend it right now. Please contact your loan officer.",
    "暂时无法重新发送，请联系您的贷款专员。",
    "No pudimos reenviarlo en este momento. Comuníquese con su oficial de préstamos.",
  ],
} as const;

export type MsgKey = keyof typeof S;
export type Translate = (key: MsgKey, vars?: Record<string, string | number>) => string;

const COLUMN: Record<Lang, number> = { en: 0, zh: 1, es: 2 };

export const translate = (lang: Lang, key: MsgKey, vars?: Record<string, string | number>): string => {
  const entry = S[key];
  let text: string = entry[COLUMN[lang]] || entry[0];
  if (vars) {
    Object.keys(vars).forEach((k) => {
      text = text.split(`{${k}}`).join(String(vars[k]));
    });
  }
  return text;
};

// Form 1003, Section 5 (declarations A, B, C, D1, D2, E-M), in the order of
// DECLARATION_KEYS. The English is the form's own wording.
export const DECLARATION_QUESTIONS: Record<Lang, string[]> = {
  en: [
    "Will you occupy the property as your primary residence?",
    "If this is a Purchase Transaction: Do you have a family relationship or business affiliation with the seller of the property?",
    "Are you borrowing any money for this real estate transaction (e.g., money for your closing costs or down payment) or obtaining any money from another party, such as the seller or realtor, that you have not disclosed on this loan application?",
    "Have you or will you be applying for a mortgage loan on another property (not the property securing this loan) on or before closing this transaction that is not disclosed on this loan application?",
    "Have you or will you be applying for any new credit (e.g., installment loan, credit card, etc.) on or before closing this loan that is not disclosed on this application?",
    "Will this property be subject to a lien that could take priority over the first mortgage lien, such as a clean energy lien paid through your property taxes (e.g., the Property Assessed Clean Energy Program)?",
    "Are you a co-signer or guarantor on any debt or loan that is not disclosed on this application?",
    "Are there any outstanding judgments against you?",
    "Are you currently delinquent or in default on a Federal debt?",
    "Are you a party to a lawsuit in which you potentially have any personal financial liability?",
    "Have you conveyed title to any property in lieu of foreclosure in the past 7 years?",
    "Within the past 7 years, have you completed a pre-foreclosure sale or short sale, whereby the property was sold to a third party and the Lender agreed to accept less than the outstanding mortgage balance due?",
    "Have you had property foreclosed upon in the last 7 years?",
    "Have you declared bankruptcy within the past 7 years?",
  ],
  zh: [
    "您是否会将该房产作为您的主要居所？",
    "如果这是一笔购房交易：您与该房产的卖方是否有亲属关系或业务关联？",
    "您是否为本次房地产交易借入任何款项（例如用于支付过户费用或首付款的资金），或从其他方（例如卖方或房地产经纪人）获得任何款项，而这些款项未在本贷款申请中披露？",
    "在本交易完成过户之前或当天，您是否已经或将要为另一处房产（并非本贷款的抵押房产）申请房贷，而该申请未在本贷款申请中披露？",
    "在本贷款完成过户之前或当天，您是否已经或将要申请任何新的信贷（例如分期贷款、信用卡等），而该申请未在本申请中披露？",
    "该房产是否会附有可能优先于第一顺位房贷留置权的留置权，例如通过房产税缴纳的清洁能源留置权（如 PACE 清洁能源房产评估计划）？",
    "您是否为任何未在本申请中披露的债务或贷款的共同签署人或担保人？",
    "是否有任何针对您的尚未清偿的法院判决？",
    "您目前是否拖欠任何联邦债务或对其违约？",
    "您是否为某项诉讼的当事人，且可能需要在其中承担个人财务责任？",
    "在过去 7 年内，您是否曾以转让房产产权代替法拍（以房抵债）？",
    "在过去 7 年内，您是否曾完成法拍前出售或卖空（short sale），即房产被出售给第三方，且贷款方同意接受低于未偿房贷余额的款项？",
    "在过去 7 年内，您是否有房产被法拍（取消抵押品赎回权）？",
    "在过去 7 年内，您是否曾申请破产？",
  ],
  es: [
    "¿Ocupará la propiedad como su residencia principal?",
    "Si se trata de una transacción de compra: ¿Tiene alguna relación familiar o afiliación comercial con el vendedor de la propiedad?",
    "¿Está pidiendo dinero prestado para esta transacción de bienes raíces (por ejemplo, dinero para sus costos de cierre o pago inicial) u obteniendo dinero de otra parte, como el vendedor o el agente inmobiliario, que no haya declarado en esta solicitud de préstamo?",
    "¿Ha solicitado o solicitará un préstamo hipotecario para otra propiedad (no la propiedad que garantiza este préstamo) antes o en la fecha de cierre de esta transacción, que no haya declarado en esta solicitud de préstamo?",
    "¿Ha solicitado o solicitará algún crédito nuevo (por ejemplo, préstamo a plazos, tarjeta de crédito, etc.) antes o en la fecha de cierre de este préstamo, que no haya declarado en esta solicitud?",
    "¿Estará esta propiedad sujeta a un gravamen que podría tener prioridad sobre el gravamen de la primera hipoteca, como un gravamen de energía limpia pagado a través de sus impuestos a la propiedad (por ejemplo, el programa Property Assessed Clean Energy)?",
    "¿Es usted cofirmante o garante de alguna deuda o préstamo que no haya declarado en esta solicitud?",
    "¿Existe alguna sentencia judicial pendiente en su contra?",
    "¿Está actualmente atrasado o en incumplimiento de pago de una deuda federal?",
    "¿Es usted parte en una demanda en la que podría tener alguna responsabilidad financiera personal?",
    "¿Ha transferido el título de alguna propiedad en lugar de una ejecución hipotecaria en los últimos 7 años?",
    "En los últimos 7 años, ¿ha completado una venta previa a la ejecución hipotecaria o una venta corta (short sale), en la que la propiedad se vendió a un tercero y el prestamista aceptó recibir menos del saldo hipotecario pendiente?",
    "¿Ha tenido alguna propiedad en ejecución hipotecaria en los últimos 7 años?",
    "¿Se ha declarado en bancarrota en los últimos 7 años?",
  ],
};

// ?lang=zh|es|en wins, then the borrower's last choice, then the browser's
// language; English otherwise.
const STORAGE_KEY = "gop-lang";

const asLang = (v?: string | null): Lang | null => {
  const s = `${v || ""}`.trim().toLowerCase();
  if (s.startsWith("zh")) return "zh";
  if (s.startsWith("es")) return "es";
  if (s.startsWith("en")) return "en";
  return null;
};

export const initialLang = (search: string): Lang => {
  let saved: string | null = null;
  try {
    saved = window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    saved = null;
  }
  return (
    asLang(new URLSearchParams(search).get("lang")) ||
    asLang(saved) ||
    asLang(typeof navigator !== "undefined" ? navigator.language : "") ||
    "en"
  );
};

export const rememberLang = (lang: Lang) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch (e) {
    // private mode; the choice lasts for this page only
  }
};

export const LangContext = React.createContext<Lang>("en");

export const useT = (): Translate => {
  const lang = useContext(LangContext);
  return (key, vars) => translate(lang, key, vars);
};
